import type { OrderQueue } from '@elutide/shared';
import { api } from '@/lib/api';

/**
 * /orders/queues returns `{ [queue]: { count, metric_id } }`. Callers that only need the
 * number use this so the shape is normalised in one place (and a plain number is also
 * accepted, for older fixtures).
 *
 * It has its own cache key. `features/orders` caches the raw response under
 * ['orders','queues'], and two query functions sharing one key would let a raw
 * `{count, metric_id}` object reach a caller expecting a number, which renders as a crash.
 * The key still sits under the ['orders'] prefix, so the /changes poller invalidates it.
 */
export const QUEUE_COUNTS_KEY = ['orders', 'queues', 'counts'] as const;

export async function fetchQueueCounts(): Promise<Record<OrderQueue, number>> {
  const raw = await api.get<Record<string, number | { count: number; metric_id?: string }>>('/orders/queues');
  const out = {} as Record<OrderQueue, number>;
  for (const [queue, value] of Object.entries(raw)) {
    const count = typeof value === 'number' ? value : Number(value?.count ?? 0);
    out[queue as OrderQueue] = Number.isFinite(count) ? count : 0;
  }
  return out;
}
